import {
  Button,
  Input,
  InputRef,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Modal as ModalAntd,
  Typography,
  notification,
} from "antd";
import type { ColumnType, ColumnsType } from "antd/es/table";
import type { FilterConfirmProps } from "antd/es/table/interface";
import {
  DeleteOutlined,
  FileOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  SearchOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { Link, useParams } from "react-router-dom";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import {
  SupplierFile,
  SupplierFileTable,
  del,
  get,
  put,
} from "../../../../../api";
import Highlighter from "react-highlight-words";
import SupplierFilesForm from "../detail";
import Modal from "../../../../../components/general/modal";
import { CanShow } from "../../../../../components";
import RepseForm from "../../../repse";

const label = "archivo";

const entries = [
  { key: "repse", label: "REPSE" },
  { key: "tarjeta", label: "Tarjeta de Identificación Patronal" },
  { key: "cedula", label: "Cédula de Identificación Fiscal" },
];

const SupplierFilesList = () => {
  const { Text } = Typography;
  const { confirm } = ModalAntd;

  const [innerFiles, setInnerFiles] = useState<SupplierFile[]>([]);
  const [files, setFiles] = useState<SupplierFileTable[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [titleModal, setTitleModal] = useState("");
  const [contextModal, setContextModal] = useState<ReactElement | null>(null);
  const searchInput = useRef<InputRef>(null);
  const { id } = useParams();

  const getFilesCallback = React.useCallback(async () => {
    const http = await get<SupplierFile[]>(`/suppliers/${id}/files`);
    if (http.statusCode === 200) {
      setInnerFiles(() => http.response);
      let files: SupplierFileTable[] = [];
      entries.forEach((entry) => {
        const file = http.response.find((file) => file.name_key === entry.key);
        if (file) {
          files.push({
            name: file.name,
            name_description: file.name_description,
            status: file.is_valid ? "completo" : "por_verificar",
            fileUrl: file.fileUrl,
            created_at: file.created_at,
            key: entry.key,
            expiration_date: file.expiration_date,
          });
        } else {
          files.push({
            name: "-",
            name_description: entry.label,
            status: "sin_archivo",
            fileUrl: "",
            created_at: undefined,
            key: entry.key,
            expiration_date: undefined,
          });
        }
      });
      setFiles(files);
    }
    setFilesLoading(false);
  }, [id]);

  useEffect(() => {
    if (id !== undefined) getFilesCallback();
  }, [id, getFilesCallback]);

  useEffect(() => {
    getFilesCallback();
  }, [contextModal, getFilesCallback]);
  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: keyof SupplierFileTable
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex as string);
  };
  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };
  const getColumnSearchProps = (
    dataIndex: keyof SupplierFileTable
  ): ColumnType<SupplierFileTable> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => {
              clearFilters && handleReset(clearFilters);
              handleSearch(selectedKeys as string[], confirm, dataIndex);
            }}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Cerrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      (record[dataIndex] as string)
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const repseModal = (typeForm: "add" | "edit" | "verificar") => {
    setModalIsVisible(true);
    setModalIsVisible(true);
    setTitleModal(
      `${
        typeForm === "add"
          ? "Agregar"
          : typeForm === "edit"
          ? "Editar"
          : "Verificar"
      } ${label}`
    );
    setContextModal(() => (
      <RepseForm
        type={typeForm}
        supplierId={id ? Number(id) : undefined}
        add={(data) => {
          setFiles((files) =>
            files.map((file) => {
              if (file.key === "repse") {
                return {
                  ...file,
                  fileUrl: file.fileUrl,
                  status: data.is_valid ? "completo" : "por_verificar",
                  expiration_date: data.expiration_date,
                };
              }
              return file;
            })
          );
          setModalIsVisible(false);
        }}
        update={(data) => {
          setModalIsVisible(false);
          setContextModal(null);
          setFiles((files) =>
            files.map((file) => {
              if (file.key === "repse") {
                return {
                  ...file,
                  fileUrl: file.fileUrl,
                  status: data.is_valid ? "completo" : "por_verificar",
                  expiration_date: data.expiration_date,
                };
              }
              return file;
            })
          );
        }}
      />
    ));
  };

  const showModal = (
    type: string,
    row: SupplierFileTable,
    file?: SupplierFile
  ) => {
    setModalIsVisible(true);
    setTitleModal(
      `${
        type === "add" ? "Agregar" : type === "edit" ? "Editar" : "Ver"
      } ${label}`
    );
    setContextModal(() => (
      <SupplierFilesForm
        row={row}
        type={type}
        file={file}
        add={(e) => {
          if (e) {
            setFiles((files) =>
              files.map((file) => {
                if (file.key === e.name_key) {
                  return {
                    ...file,
                    status: e.is_valid ? "completo" : "por_verificar",
                    name: e.name,
                    fileUrl: e.fileUrl,
                    created_at: e.created_at,
                    key: e.name_key,
                  };
                }
                return file;
              })
            );
            setInnerFiles((innerFiles) => [...innerFiles, e]);
            setModalIsVisible(false);
          }
        }}
        update={(e) => {
          if (e) {
            setFiles((files) =>
              files.map((file) => {
                if (file.key === e.name_key) {
                  return {
                    ...file,
                    status: e.is_valid ? "completo" : "por_verificar",
                    name: e.name,
                    fileUrl: e.fileUrl,
                    created_at: e.created_at,
                  };
                }
                return file;
              })
            );
            setModalIsVisible(false);
          }
        }}
      />
    ));
  };

  const handleDelete = (key: string) => {
    confirm({
      title: `Eliminar ${label}`,
      icon: <ExclamationCircleOutlined />,
      content: `¿Estás seguro que deseas borrar el ${label} seleccionado?, no podrá recuperarlo después.`,
      okText: "Sí",
      okType: "danger",
      cancelText: "No",
      onOk() {
        let fileToDelete = innerFiles.find((f) => f.name_key === key);

        if (fileToDelete !== undefined) {
          del(`/files/${fileToDelete.id}`).then((res) => {
            if (res.statusCode === 200) {
              notification.success({
                message: `Se ha eliminado el ${label} correctamente.`,
              });
              // refresh files
              setFiles((files) =>
                files.map((file) => {
                  if (file.key === key) {
                    return {
                      ...file,
                      status: "sin_archivo",
                      name: "-",
                      fileUrl: "",
                      created_at: undefined,
                    };
                  }
                  return file;
                })
              );
            }
          });
        }
      },
      onCancel() {},
    });
  };

  const handleVerification = (key: string, isValid: 0 | 1) => {
    confirm({
      title: !isValid ? `Verificar ${label}` : `Invalidar ${label}`,
      icon: <ExclamationCircleOutlined />,
      content: !isValid
        ? `¿Estás seguro que deseas verificar el ${label} seleccionado?, no se podrá modificar después.`
        : `¿Estás seguro que deseas marcar como inválido el ${label} seleccionado?`,
      okText: "Sí",
      okType: "danger",
      cancelText: "No",
      onOk() {
        const fileToVerify = innerFiles.find((f) => f.name_key === key);

        if (fileToVerify) {
          put(`/files/${fileToVerify.id}`, { is_valid: !isValid }).then(
            (res) => {
              if (res.statusCode === 200) {
                notification.success({
                  message: !isValid
                    ? `Se ha verificado el ${label} correctamente.`
                    : `El ${label} se ha marcado como inválido.`,
                });

                setFiles((files) =>
                  files.map((file) => {
                    if (file.key === key) {
                      return {
                        ...file,
                        status: !isValid ? "completo" : "por_verificar",
                      };
                    }
                    return file;
                  })
                );
              }
            }
          );
        }
      },
      onCancel() {},
    });
  };

  const modalCallback = React.useCallback(
    (file: any, record: SupplierFileTable) => {
      get<SupplierFile[]>(`/suppliers/${id}/files`).then((e) => {
        const resp = e.response;
        let algo = resp.find((f) => f.name_key === record.key);
        showModal("edit", file, algo);
      });
    },
    [id]
  );

  const columns: ColumnsType<SupplierFileTable> = [
    {
      title: "Nombre",
      dataIndex: "name_description",
      key: "name_description",
      ...getColumnSearchProps("name_description"),
      render: (name_description) => <Text>{name_description}</Text>,
    },
    {
      title: "Estatus",
      key: "status",
      dataIndex: "status",
      filters: [
        { text: "SIN ARCHIVO", value: "sin_archivo" },
        { text: "POR VERIFICAR", value: "por_verificar" },
        { text: "COMPLETO", value: "completo" },
      ],
      onFilter: (value, record) => record.status.startsWith(value as string),
      render: (status: string) => (
        <Row gutter={16} style={{ width: 190 }}>
          <Tag
            color={
              status === "completo"
                ? "green"
                : status === "por_verificar"
                ? "orange"
                : "red"
            }
            key={status}
            style={{ marginBottom: 10 }}
          >
            {status.replace("_", " ").toUpperCase()}
          </Tag>
        </Row>
      ),
    },
    {
      title: "Archivo",
      dataIndex: "fileUrl",
      key: "fileUrl",
      ...getColumnSearchProps("fileUrl"),
      render: (fileUrl, rec) => (
        <Link to={fileUrl} target="_blank">
          {rec.name}
        </Link>
      ),
    },
    {
      title: "Fecha de creación",
      dataIndex: "created_at",
      key: "created_at",
      render: (created_at) => (
        <>{created_at ? moment.unix(created_at).format("DD/MM/YYYY") : "-"}</>
      ),
    },
    {
      title: "Fecha de expiración",
      dataIndex: "expiration_date",
      key: "expiration_date",
      render: (expiration_date) => (
        <>
          {expiration_date
            ? moment.unix(expiration_date).format("DD/MM/YYYY")
            : "N/A"}
        </>
      ),
    },
    {
      title: "Acciones",
      key: "sup",
      fixed: "right",
      width: 170,
      align: "center",
      render: (file, record) => {
        const baseStyle = { marginLeft: 10 };
        const primaryBlueStyle = {
          ...baseStyle,
          backgroundColor: "#1890ff",
          borderColor: "#1890ff",
        };
        const successGreenStyle = {
          ...baseStyle,
          backgroundColor: "#68d78a",
          borderColor: "#68d78a",
        };

        const isRepse = record.key === "repse";
        const isWithoutFile = record.status === "sin_archivo";
        const isPendingVerification = record.status === "por_verificar";
        const isCompleted = record.status === "completo";

        return (
          <>
            {/* UPLOAD / EDIT (SUPPLIER) */}
            {isWithoutFile && (
              <CanShow roles={["Supplier"]}>
                <Tooltip title="Upload file">
                  <Button
                    type="primary"
                    size="small"
                    icon={<FileOutlined />}
                    style={primaryBlueStyle}
                    onClick={() =>
                      isRepse ? repseModal("add") : showModal("add", file)
                    }
                  />
                </Tooltip>
              </CanShow>
            )}

            {!isWithoutFile && isRepse && (
              <CanShow roles={["Supplier"]}>
                <Tooltip title="Edit file">
                  <Button
                    type="primary"
                    size="small"
                    icon={<FileOutlined />}
                    style={primaryBlueStyle}
                    onClick={() => repseModal("edit")}
                  />
                </Tooltip>
              </CanShow>
            )}

            {(isPendingVerification && !isRepse && record.fileUrl) && (
              <CanShow roles={["Supplier"]}>
                <Tooltip title="Edit file">
                  <Button
                    ghost
                    type="primary"
                    size="small"
                    icon={<EditOutlined />}
                    style={baseStyle}
                    onClick={() => modalCallback(file, record)}
                  />
                </Tooltip>
              </CanShow>
            )}

            {/* COMPLETED */}
            {isCompleted && !isRepse && (
              <>
                <CanShow roles={["Admin", "Financial Advisor"]}>
                  <Tooltip title="Cambiar estatus">
                    <Button
                      style={{
                        marginLeft: 10,
                      }}
                      color="danger"
                      variant="solid"
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={() =>
                        handleVerification(
                          record.key,
                          record.status === "completo" ? 1 : 0
                        )
                      }
                    />
                  </Tooltip>
                </CanShow>

                <Tooltip title="Completed">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={successGreenStyle}
                  />
                </Tooltip>
              </>
            )}

            {isCompleted && isRepse && (
              <CanShow roles={["Admin", "Financial Advisor", "Customer"]}>
                <Tooltip title="Completed">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckCircleOutlined />}
                    style={successGreenStyle}
                    onClick={() => repseModal("verificar")}
                  />
                </Tooltip>
              </CanShow>
            )}

            {/* VERIFY */}
            {isPendingVerification && (
              <CanShow roles={["Admin", "Financial Advisor"]}>
                <Tooltip title="Verify">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    style={baseStyle}
                    onClick={() =>
                      isRepse
                        ? repseModal("verificar")
                        : handleVerification(
                            record.key,
                            record.status === "completo" ? 1 : 0
                          )
                    }
                  />
                </Tooltip>
              </CanShow>
            )}

            {/* DELETE */}
            {record.name !== "-" && !isCompleted && !isRepse && (
              <Tooltip title="Delete">
                <Button
                  danger
                  type="primary"
                  size="small"
                  icon={<DeleteOutlined />}
                  style={baseStyle}
                  onClick={() => handleDelete(record.key)}
                />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Spin spinning={filesLoading} tip={<p>Cargando...</p>}>
      {!filesLoading ? (
        <Table
          columns={columns}
          dataSource={files}
          scroll={{ x: 1000 }}
          pagination={false}
        />
      ) : null}
      <Modal
        title={titleModal}
        isVisible={modalIsVisible}
        setIsVisible={setModalIsVisible}
        width={1000}
      >
        {contextModal}
      </Modal>
    </Spin>
  );
};

export default SupplierFilesList;
